import type {ValueType} from '@github-ui/custom-properties-types'
import {areEqualValues} from '@github-ui/custom-properties-types/helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

interface Suggestion {
  singleSelectValues: string[]
  multiSelectValues: string[]
}

interface Result {
  suggestion: Suggestion | undefined
}

interface MutationVariables {
  propertyName: string
  description: string
  existingAllowedValues: string[]
}

interface HookParams {
  sourceName: string
  valueType: ValueType
  onSuggestionSuccess: () => void
}

export function useAllowedValuesSuggester({sourceName, valueType, onSuggestionSuccess}: HookParams) {
  const mutation = useMutation<Result, Error, MutationVariables>({
    mutationKey: ['copilotSuggestAllowedValues', sourceName],
    mutationFn: async variables => {
      const response = await verifiedFetchJSON(copilotSuggestAllowedValuesPath({org: sourceName}), {
        method: 'POST',
        body: {
          propertyName: variables.propertyName,
          description: variables.description,
          existingAllowedValues: variables.existingAllowedValues,
        },
      })

      if (!response.ok) {
        throw new Error()
      }

      return response.json()
    },
    onSuccess: onSuggestionSuccess,
  })

  function suggestAllowedValues(variables: MutationVariables) {
    if (!isFeatureEnabled('copilot_suggest_allowed_values')) {
      return
    }

    if (shouldFetchSuggestion(mutation.variables, variables)) {
      mutation.mutate(variables)
    }
  }

  const suggestion = mutation.data?.suggestion
  const isSelect = valueType === 'single_select' || valueType === 'multi_select'

  if (!isSelect || !suggestion) {
    return {suggestedAllowedValues: [], suggestAllowedValues}
  }

  const suggestedAllowedValues =
    valueType === 'single_select' ? suggestion.singleSelectValues : suggestion.multiSelectValues

  return {suggestedAllowedValues, suggestAllowedValues}
}

const copilotSuggestAllowedValuesPath = ({org}: {org: string}) =>
  `/organizations/${encodeURIComponent(org)}/settings/custom-properties/suggest-allowed-values`

const normalize = (value: string) => value.toLowerCase()
const containsSameValues = (arr1: string[], arr2: string[]) =>
  areEqualValues(arr1.map(normalize).toSorted(), arr2.map(normalize).toSorted())

function variablesAreDifferent(lastVars: MutationVariables | undefined, nextVars: MutationVariables) {
  if (!lastVars) {
    return true
  }

  return (
    lastVars.propertyName !== nextVars.propertyName ||
    lastVars.description !== nextVars.description ||
    !containsSameValues(lastVars.existingAllowedValues, nextVars.existingAllowedValues)
  )
}

const shouldFetchSuggestion = (lastVars: MutationVariables | undefined, nextVars: MutationVariables) =>
  (nextVars.propertyName.trim() || nextVars.description.trim()) && variablesAreDifferent(lastVars, nextVars)
